/**
 * A rave's own words in the reader's language, ONCE per bundle (step 73.10).
 *
 * The bundle carries each visitor-facing text as an English base plus a
 * `*Local` map; `localized` picks one, falling back to the English per text.
 * Doing it here — where the bundle is turned into view data — rather than at
 * each of the dozens of places a stage, a category or the rave's name is
 * printed means a screen cannot forget it (the same reason act names are
 * localised once, as `displayName`).
 *
 * Replaced in place: `festival.name` / `description`, `edition.name`, every
 * track's `label`, stage's `name`, map point's `name` and sponsor's `tier`.
 * Acts, artists, announcements and info pages keep their base fields — they
 * are localised where they are drawn (`displayName`, `t(base, local)`).
 * The maps stay on each object, so nothing is lost. Returns the SAME object
 * when nothing would change, so a memo keyed on it is not churned.
 */
import { localized } from './locale.js';
import type { EditionBundle } from './types.js';

type Local = Readonly<Record<string, string>> | undefined;

export function localizeBundle<B extends EditionBundle>(bundle: B, locale: string): B {
  const pick = (base: string, local: Local) => (local ? localized(base, local, locale) : base);
  const changes = (base: string | undefined, local: Local) => base !== undefined && pick(base, local) !== base;

  const any = changes(bundle.festival.name, bundle.festival.nameLocal)
    || changes(bundle.festival.description, bundle.festival.descriptionLocal)
    || changes(bundle.edition.name, bundle.edition.nameLocal)
    || bundle.tracks.some((t) => changes(t.label, t.labelLocal))
    || bundle.stages.some((s) => changes(s.name, s.nameLocal))
    || (bundle.map?.points ?? []).some((p) => changes(p.name, p.nameLocal))
    || (bundle.sponsors ?? []).some((s) => changes(s.tier, s.tierLocal));
  if (!any) return bundle;

  return {
    ...bundle,
    festival: {
      ...bundle.festival,
      name: pick(bundle.festival.name, bundle.festival.nameLocal),
      ...(bundle.festival.description !== undefined
        ? { description: pick(bundle.festival.description, bundle.festival.descriptionLocal) }
        : {}),
    },
    edition: { ...bundle.edition, name: pick(bundle.edition.name, bundle.edition.nameLocal) },
    tracks: bundle.tracks.map((t) => (changes(t.label, t.labelLocal) ? { ...t, label: pick(t.label, t.labelLocal) } : t)),
    stages: bundle.stages.map((s) => (changes(s.name, s.nameLocal) ? { ...s, name: pick(s.name, s.nameLocal) } : s)),
    ...(bundle.map
      ? {
          map: {
            ...bundle.map,
            points: bundle.map.points.map((p) => (changes(p.name, p.nameLocal) ? { ...p, name: pick(p.name, p.nameLocal) } : p)),
          },
        }
      : {}),
    ...(bundle.sponsors
      ? {
          sponsors: bundle.sponsors.map((s) => (s.tier !== undefined && changes(s.tier, s.tierLocal)
            ? { ...s, tier: pick(s.tier, s.tierLocal) } : s)),
        }
      : {}),
  };
}
